import { Guid } from 'js-guid';

/**
 * Перевод локализаций через ИИ (POST /AdminAI/TranslateEntity).
 * Источник — значения основной (русской) формы с теми же именами полей, что и в конфиге блока.
 * Переводятся text / textarea / seo / list и текстовые блоки content; картинки, файлы и галереи не трогаются.
 */

const TEXT_BLOCK = 'TextContentItem';
const SEO_KEYS = ['title', 'description', 'keywords'];

const listItemText = (item, itemKey) => (typeof item === 'string' ? item : item?.[itemKey] || '');

const textBlocksOf = (content) =>
  (content?.items || []).filter(
    (i) => i.discriminator === TEXT_BLOCK && String(i.content || '').trim(),
  );

/**
 * Тело запроса из основной формы. null — переводить нечего
 */
export const buildTranslateRequest = (fields, source, targetLanguages, context) => {
  const flat = {};
  const contentBlocks = [];

  fields.forEach((field) => {
    const value = source?.[field.name];
    switch (field.type) {
      case 'text':
      case 'textarea':
      case undefined:
        if (typeof value === 'string' && value.trim()) flat[field.name] = value;
        break;
      case 'seo':
        SEO_KEYS.forEach((key) => {
          if (value?.[key]?.trim?.()) flat[`${field.name}.${key}`] = value[key];
        });
        break;
      case 'list': {
        const itemKey = field.itemKey || 'title';
        (value || []).forEach((item, index) => {
          const text = listItemText(item, itemKey);
          if (text.trim()) flat[`${field.name}.${index}`] = text;
        });
        break;
      }
      case 'content':
        textBlocksOf(value).forEach((block) => {
          contentBlocks.push({ key: `${field.name}::${block.guid}`, html: block.content });
        });
        break;
      default:
        break;
    }
  });

  if (!Object.keys(flat).length && !contentBlocks.length) return null;
  return { fields: flat, contentBlocks, targetLanguages, sourceLanguage: 'RU', context };
};

/**
 * Перевод → поля локализации. Существующий контент перевода не теряет медиа-блоки:
 * его текстовые блоки по порядку заменяются переведёнными, лишние переведённые добавляются в конец.
 */
export const applyTranslation = (localization, fields, source, translation) => {
  const translatedFields = translation?.fields || {};
  const translatedBlocks = new Map((translation?.contentBlocks || []).map((b) => [b.key, b.html]));
  const next = { ...localization };
  let contentChanged = false;

  fields.forEach((field) => {
    const value = source?.[field.name];
    switch (field.type) {
      case 'text':
      case 'textarea':
      case undefined:
        if (field.name in translatedFields) next[field.name] = translatedFields[field.name];
        break;
      case 'seo': {
        const seo = {
          title: '',
          description: '',
          keywords: '',
          ...(localization[field.name] || {}),
        };
        SEO_KEYS.forEach((key) => {
          const translated = translatedFields[`${field.name}.${key}`];
          if (translated !== undefined) seo[key] = translated;
        });
        next[field.name] = seo;
        break;
      }
      case 'list': {
        const itemKey = field.itemKey || 'title';
        const items = (value || [])
          .map(
            (item, index) =>
              translatedFields[`${field.name}.${index}`] ?? listItemText(item, itemKey),
          )
          .filter((text) => String(text).trim())
          .map((text) => ({ [itemKey]: text }));
        if (items.length) next[field.name] = items;
        break;
      }
      case 'content': {
        const translatedTexts = textBlocksOf(value)
          .map((block) => translatedBlocks.get(`${field.name}::${block.guid}`))
          .filter((html) => html !== undefined);
        if (!translatedTexts.length) break;

        const currentItems = localization[field.name]?.items || [];
        const items = [];
        let textIndex = 0;
        currentItems.forEach((item) => {
          if (item.discriminator !== TEXT_BLOCK) {
            items.push(item);
          } else if (textIndex < translatedTexts.length) {
            items.push({ ...item, content: translatedTexts[textIndex++] });
          }
          // Лишние старые текстовые блоки перевода отбрасываем
        });
        translatedTexts.slice(textIndex).forEach((html) => {
          items.push({ guid: Guid.newGuid().toString(), discriminator: TEXT_BLOCK, content: html });
        });

        next[field.name] = { ...(localization[field.name] || {}), items };
        contentChanged = true;
        break;
      }
      default:
        break;
    }
  });

  // ContentEditor читает значение только при монтировании — новый ключ пересоздаёт его
  if (contentChanged) next._aiVersion = (localization._aiVersion || 0) + 1;
  return next;
};
